import type { z } from 'zod';
import {
  useUserTableFilterConfig,
  useUpsertUserTableFilterConfig,
} from '@/api/rpc/hooks/core/useUserTableFilterConfig';

/**
 * Persist a table's chosen filter settings per user so they survive across view
 * sessions (and devices), backed by the generic `core_user_table_filter_configs`
 * store. Reusable for any table: pass a stable `tableId` and a Zod schema that
 * describes the filter shape you want to remember.
 *
 * The page stays the source of truth for the *live* filter state (typically the
 * URL search params); this hook only records the last-used values and hands
 * them back so the page can seed its defaults on a fresh visit — mirroring the
 * procurement "saved preference seeds the URL" pattern.
 *
 * (No manual memoization here: the React Compiler caches these derived values
 * and callbacks for us. `schema` should still be a stable module-scope
 * reference so the compiler's parse cache actually hits.)
 */
export function useTableFilterPreferences<
  TFilters extends Record<string, unknown>,
>(tableId: string, schema: z.ZodType<TFilters>) {
  const { data, isPending } = useUserTableFilterConfig(tableId);
  const { mutate } = useUpsertUserTableFilterConfig();

  // Parse through the caller's schema so unknown/removed fields are dropped and
  // newly-added fields fall back to their defaults for older stored rows.
  const parsed = data ? schema.safeParse(data.filters) : null;
  const savedFilters: TFilters | null = parsed?.success ? parsed.data : null;

  const saveFilters = (filters: TFilters) => {
    mutate({ tableId, filters });
  };

  const clearFilters = () => {
    mutate({ tableId, filters: {} });
  };

  return {
    savedFilters,
    saveFilters,
    clearFilters,
    // `isPending` (not `isLoading`) so a fresh mount whose fetch hasn't started
    // is still treated as "not yet known" — callers must wait for this before
    // writing a default, or they'd clobber the saved value before it loads.
    isPending,
  };
}
